import { format, isToday } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

interface HabitGridCellProps {
  date: Date | null;
  isCompleted: boolean;
  canToggle: boolean;
  onClick: () => void;
  onInfoClick?: (event: React.MouseEvent) => void;
  color: {
    empty: string;
    filled: string;
  };
}

export default function HabitGridCell({
  date,
  isCompleted,
  canToggle,
  onClick,
  onInfoClick,
  color,
}: HabitGridCellProps) {
  const isMobile = useIsMobile();
  
  if (!date) return <div className="w-4 h-4 rounded-sm opacity-0" />; // Transparent but takes space
  
  const dateStr = format(date, 'MMMM d, yyyy');
  const completionStatus = isCompleted ? 'Completed' : 'Not completed';
  const cellSize = isMobile ? "w-3 h-3" : "w-4 h-4";
  const isTodayDate = isToday(date);
  
  const glowEffect = isCompleted ? "shadow-[0_0_5px_0px_rgba(255,255,255,0.3)] dark:shadow-[0_0_8px_0px_rgba(255,255,255,0.15)]" : "";
  const hoverEffect = canToggle 
    ? "hover:opacity-80 hover:scale-110 hover:z-10 cursor-pointer" 
    : onInfoClick ? "hover:scale-125 hover:z-10 hover:shadow-lg cursor-pointer" : "cursor-default";
  
  // Today's border styling
  const todayBorder = isTodayDate ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : "";
  
  // Visual indicator for clickable cells
  const clickableIndicator = (canToggle || onInfoClick) 
    ? "after:absolute after:inset-0 after:opacity-0 after:hover:opacity-30 after:rounded-sm after:transition-opacity"
    : "";
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    
    if (canToggle) {
      onClick();
    } else if (onInfoClick) {
      onInfoClick(e); // Pass the event object to the handler
    }
  };
  
  return (
    <div
      onClick={handleClick}
      className={`${cellSize} rounded-sm transition-all duration-200 relative ${glowEffect} ${hoverEffect} ${clickableIndicator} ${todayBorder} habit-grid-cell`}
      style={{ 
        backgroundColor: isCompleted ? color.filled : color.empty,
      }}
      title={`${dateStr} - ${completionStatus}${isTodayDate ? ' (Today)' : ''}`}
      data-date={dateStr}
      data-testid="habit-cell"
      role="button"
      tabIndex={0}
    />
  );
}
